import { inject, Injectable } from "@angular/core";
import { AuthService as Auth0Service } from "@auth0/auth0-angular";
import {
  AuthStore,
  EMPTY_STATE_MESSAGES,
  STORAGE_KEYS,
  StorageService,
  ThemeConfig,
} from "@rr/shared-data-access-core";
import { catchError, filter, lastValueFrom, map, of, switchMap, take, tap, timeout } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../environments/environment";
import { Router } from "@angular/router";
import { toObservable } from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: "root",
})
export class AppInitilizerService {
  // DI
  readonly #authStore = inject(AuthStore);
  readonly #authService = inject(Auth0Service);
  readonly #router = inject(Router);
  readonly #http = inject(HttpClient);
  readonly #storageService = inject(StorageService);

  private readonly memberUser$ = toObservable(this.#authStore.memberUser).pipe(
    filter((memberUser) => !!memberUser),
    take(1),
  );

  private setupUser() {
    return this.#authService.user$.pipe(
      timeout(5000),
      take(1),
      catchError(() => of(null)),
      map((res) => {
        this.#authStore.setUser(res ?? null);
        return res;
      }),
    );
  }

  private setupTheme() {
    return this.#http.get(environment.themeUrl.replace("{tenant}", this.#authStore.tenant() ?? "")).pipe(
      take(1),
      catchError(() => {
        this.#authStore.setCallback(() =>
          this.#router.navigate(["error"], {
            queryParams: { error_description: EMPTY_STATE_MESSAGES.NO_THEME, main_action: false },
          }),
        );
        return of(null);
      }),
      map((res: object | null) => {
        this.#authStore.setTheme(res as unknown as ThemeConfig);
        return res as unknown as ThemeConfig;
      }),
    );
  }

  initializeApp(): Promise<unknown> {
    this.#authStore.setTheme(null);

    if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
      this.#authStore.setCallback(() => this.#router.navigate(["shell"]));
    }

    return lastValueFrom(
      this.#authService.isAuthenticated$.pipe(
        timeout(5000),
        take(1),
        catchError(() => of(false)),
        switchMap((isAuthenticated) => {
          // Issue S2301: Methods should not contain selector parameters
          // Disable SONAR reason: Can't change this isAuthenticated selector as it comes from auth0
          // prettier-ignore
          if (isAuthenticated) { // NOSONAR
            return this.#authService.getAccessTokenSilently().pipe(
              timeout(5000),
              take(1),
              catchError(() => of(null)),
              switchMap((token) => {
                this.#authStore.setToken(token);
                return this.setupUser();
              }),
              switchMap(() => {
                if (this.#authStore.user()) {
                  return this.setupTheme();
                }
                return of(null)
              }),
              switchMap(() => {
                if (this.#authStore.theme()) {
                  this.#authStore.loadMemberByUser();
                  return this.memberUser$.pipe(
                    tap(() => {
                      window.pendo?.initialize({
                        visitor: {
                          id: this.#authStore.user()?.email || "unknown",
                          email: this.#authStore.user()?.email || "unknown",
                          user_name: this.#authStore.primaryOrganization()?.fullName || "unknown"
                        },
                        account: {
                          id: this.#authStore.primaryOrganization()?.orgId || "unknown",
                          name: this.#authStore.primaryOrganization()?.companyName || "unknown",
                        }
                      });
                    })
                  );
                } else {
                  return of(null);
                }
              }),
            );
          } else {
            this.#storageService.remove(STORAGE_KEYS.tld);
            return of(null);
          }
        }),
      ),
    ).catch((err) => console.error("Initialization failed", err));
  }
}
